import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const CAPSTONE = '#E85D75';
const SYMBOLIC = '#DCB13C';
const INTEGRATION = '#57BDA2';
const TEXT_MUTED = '#5a5f7d';

interface LensRow {
  label: string;
  color: string;
  pathType: 'descend' | 'ascend' | 'vshape';
  baseY: number;
}

export function NarrativeLens() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 320;
    const eventXs = [100, 170, 240]; // 3 event positions
    const dotR = 6;
    const rowHeight = 60;
    const startY = 35;

    const lenses: LensRow[] = [
      { label: 'Tragedy', color: CAPSTONE, pathType: 'descend', baseY: startY },
      { label: 'Hero', color: SYMBOLIC, pathType: 'ascend', baseY: startY + rowHeight },
      { label: 'Redemption', color: INTEGRATION, pathType: 'vshape', baseY: startY + rowHeight * 2 },
    ];

    // Event labels
    const events = ['loss', 'struggle', 'change'];

    // Draw each lens row
    lenses.forEach((lens, lensIdx) => {
      const rowGroup = svg.append('g').attr('class', `lens-${lensIdx}`);

      // Row label
      rowGroup
        .append('text')
        .attr('x', 16)
        .attr('y', lens.baseY + 22)
        .attr('text-anchor', 'start')
        .attr('font-size', 9)
        .attr('font-weight', 600)
        .attr('fill', lens.color)
        .attr('opacity', 0)
        .text(lens.label)
        .transition()
        .duration(300)
        .delay(lensIdx * 400)
        .attr('opacity', 0.9);

      // Compute event dot Y positions based on lens path type
      let dotYs: number[];
      switch (lens.pathType) {
        case 'descend':
          dotYs = [lens.baseY + 10, lens.baseY + 24, lens.baseY + 42];
          break;
        case 'ascend':
          dotYs = [lens.baseY + 38, lens.baseY + 22, lens.baseY + 8];
          break;
        case 'vshape':
          dotYs = [lens.baseY + 10, lens.baseY + 42, lens.baseY + 14];
          break;
      }

      // Draw connecting path line
      const lineGen = d3.line<[number, number]>()
        .x((d) => d[0])
        .y((d) => d[1])
        .curve(d3.curveCatmullRom.alpha(0.5));

      const pathPoints: [number, number][] = eventXs.map((ex, i) => [ex, dotYs[i]]);

      const path = rowGroup
        .append('path')
        .attr('d', lineGen(pathPoints))
        .attr('fill', 'none')
        .attr('stroke', lens.color)
        .attr('stroke-width', 2)
        .attr('opacity', 0);

      // Get path length for animation
      const pathNode = path.node();
      if (pathNode) {
        const totalLength = pathNode.getTotalLength();
        path
          .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
          .attr('stroke-dashoffset', totalLength)
          .transition()
          .duration(600)
          .delay(200 + lensIdx * 400)
          .attr('opacity', 0.7)
          .attr('stroke-dashoffset', 0);
      }

      // Draw event dots
      eventXs.forEach((ex, i) => {
        rowGroup
          .append('circle')
          .attr('cx', ex)
          .attr('cy', dotYs[i])
          .attr('r', 0)
          .attr('fill', lens.color)
          .attr('opacity', 0.85)
          .transition()
          .duration(300)
          .delay(300 + lensIdx * 400 + i * 100)
          .attr('r', dotR);

        // Event label (only on first row to avoid clutter)
        if (lensIdx === 0) {
          svg.append('text')
            .attr('x', ex)
            .attr('y', startY - 12)
            .attr('text-anchor', 'middle')
            .attr('font-size', 8)
            .attr('class', 'viz-text-muted')
            .attr('opacity', 0)
            .text(events[i])
            .transition()
            .duration(300)
            .delay(200 + i * 100)
            .attr('opacity', 0.7);
        }
      });

      // Separator line between rows (except last)
      if (lensIdx < lenses.length - 1) {
        const sepY = lens.baseY + rowHeight - 5;
        rowGroup
          .append('line')
          .attr('x1', 10)
          .attr('y1', sepY)
          .attr('x2', width - 10)
          .attr('y2', sepY)
          .attr('stroke', TEXT_MUTED)
          .attr('stroke-width', 0.5)
          .attr('stroke-dasharray', '2,4')
          .attr('opacity', 0.25);
      }
    });

    // "Same events" bracket at top
    const bracketTimer = d3.timeout(() => {
      const bracketY = startY - 22;
      svg.append('line')
        .attr('x1', eventXs[0])
        .attr('y1', bracketY)
        .attr('x2', eventXs[0])
        .attr('y2', bracketY)
        .attr('stroke', TEXT_MUTED)
        .attr('stroke-width', 1)
        .attr('opacity', 0.5)
        .transition()
        .duration(400)
        .attr('x2', eventXs[2]);

      svg.append('text')
        .attr('x', (eventXs[0] + eventXs[2]) / 2)
        .attr('y', bracketY - 4)
        .attr('text-anchor', 'middle')
        .attr('font-size', 7)
        .attr('font-style', 'italic')
        .attr('fill', TEXT_MUTED)
        .attr('opacity', 0)
        .text('same events')
        .transition()
        .duration(300)
        .delay(200)
        .attr('opacity', 0.6);
    }, 1600);

    // Subtle pulse on dots to show "lens reframing"
    const pulseTimer = d3.timeout(() => {
      svg.selectAll('circle')
        .transition()
        .duration(400)
        .attr('r', dotR + 2)
        .transition()
        .duration(400)
        .attr('r', dotR);
    }, 2600);

    return () => {
      bracketTimer.stop();
      pulseTimer.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-book" /> Book &mdash; Narrative Lens
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 220" />
      <p className="viz-container__caption">
        Narrative frames feeling.
      </p>
    </div>
  );
}
